// ============================================================
// avaliacaoTecnica.js — a avaliação técnica por critério
//
// A antropometria vive em `avaliacaoFisica.js` e os testes de campo em
// `testesFisicos.js`. Aqui fica a terceira perna: a nota que o técnico
// da modalidade dá para cada gesto do esporte, numa escala de 1 a 10.
//
// São seis critérios por modalidade, e eles são o esporte em si. No
// tênis de mesa são golpes, no skate são manobras, no BMX são trechos
// da pista. Nada disso se mede com fita nem com cronômetro: quem olha é
// o técnico, e o que o sistema faz é guardar o olhar dele de um jeito
// que a próxima avaliação possa ser comparada com esta.
//
// ── Por que os critérios são gravados dentro da avaliação ───
//
// A lista de critérios NÃO é constante do código, é dado da avaliação.
// A planilha de 2026 mostra por que: os seis critérios técnicos ficaram
// iguais em janeiro, abril e agosto, e os quatro de capacidade física
// trocaram de nome em agosto nas três modalidades. Se a lista morasse no
// código, a nota de janeiro passaria a ser lida com o rótulo de agosto,
// e um núcleo novo com critérios próprios obrigaria a mexer no programa.
//
// Junto da lista vai gravado o divisor usado no escore, porque é por ele
// que o somatório se transforma em nota de 0 a 10. Com a lista e o
// divisor guardados, a nota de agosto de 2026 continua reproduzível
// daqui a dois anos, mesmo que os critérios mudem de novo.
//
// ── Só nota de avaliador, e nenhuma medida ──────────────────
//
// Até 22/10/2026 o BMX tinha, entre os seis critérios, dois tempos de
// pista convertidos em nota. Saíram: tempo é medida e mora em
// `testesFisicos.js`, em segundos. O que ficou aqui é o que um avaliador
// olha e pontua, nas três modalidades.
// ============================================================

// ── A escala ─────────────────────────────────────────────────
//
// A mesma escala nas três modalidades, de 1 a 10, com três faixas de
// leitura. Elas descrevem o nível do gesto, e não o atleta: a faixa de
// uma manobra recém-aprendida diz o que falta treinar nela.

export const NOTA_MINIMA = 1;
export const NOTA_MAXIMA = 10;

export const FAIXAS_DA_ESCALA = [
  { id: "regular", de: 1, ate: 3, rotulo: "Regular" },
  { id: "intermediario", de: 4, ate: 7, rotulo: "Intermediário" },
  { id: "avancado", de: 8, ate: 10, rotulo: "Avançado" },
];

export function faixaDaNota(nota) {
  if (!Number.isFinite(nota)) return null;
  return (
    FAIXAS_DA_ESCALA.find(f => nota < f.ate + 1)
    || FAIXAS_DA_ESCALA[FAIXAS_DA_ESCALA.length - 1]
  );
}

// ── A escala de cada modalidade ──────────────────────────────
//
// Os intervalos são os mesmos nas três, 1 a 3, 4 a 7 e 8 a 10, porque
// são a escala do programa, e hoje os NOMES também: regular,
// intermediário e avançado. A ficha do tênis de mesa chamava a primeira
// de iniciante e trazia uma observação sobre atletas amadores, e a
// coordenação tirou as duas em 24/10/2026.
//
// O que fica é o mecanismo: uma modalidade pode nomear as faixas dela e
// escrever uma observação própria, porque quem nomeia é quem avalia.
// Hoje nenhuma usa, e é por isso que esta função devolve a escala comum
// para as três.
export function escalaDe(modalidade) {
  const m = MODELOS_DE_CRITERIO[modalidade] || {};
  const rotulos = (m.escala && m.escala.rotulos) || {};
  return {
    faixas: FAIXAS_DA_ESCALA.map(f => ({ ...f, rotulo: rotulos[f.id] || f.rotulo })),
    observacao: (m.escala && m.escala.observacao) || null,
  };
}

// A faixa de uma nota com o nome que a modalidade dá a ela.
export function faixaDaNotaEm(nota, modalidade) {
  const faixa = faixaDaNota(nota);
  if (!faixa) return null;
  return escalaDe(modalidade).faixas.find(f => f.id === faixa.id) || faixa;
}

export function rotuloDaFaixa(nota) {
  const f = faixaDaNota(nota);
  return f ? f.rotulo : null;
}

// ── Os modelos de critério de cada modalidade ────────────────
//
// É o que a tela oferece quando se cria uma avaliação nova, e nada mais
// que isso: um ponto de partida que o técnico pode trocar antes de
// salvar. O que vale depois é a lista gravada no documento.
//
// `observa` é o que o técnico olha dentro do critério. No BMX essa
// decomposição está escrita na própria planilha, em três itens por
// trecho da pista, e ela é a diferença entre "deu 7 na largada" e "deu 7
// porque a reação é boa e a estabilidade na saída não".


// Um golpe avaliado dos dois lados vira DOIS critérios, e não um com
// média. O lado é a informação: canhoto de backhand forte e forehand
// fraco e o inverso dão a mesma média e pedem treinos opostos.
//
// O id carrega o lado para a série comparar lado com lado quando a
// ficha mudar de novo, e o rótulo escreve o gesto primeiro, porque é por
// ele que o técnico procura na tela.
function golpesDosDoisLados(golpes) {
  return golpes.flatMap(([id, rotulo]) => [
    { id: `${id}Forehand`, rotulo: `${rotulo} de forehand`, grupo: "Golpes básicos", lado: "Forehand", gesto: rotulo },
    { id: `${id}Backhand`, rotulo: `${rotulo} de backhand`, grupo: "Golpes básicos", lado: "Backhand", gesto: rotulo },
  ]);
}

export const MODELOS_DE_CRITERIO = {
  // ── Tênis de mesa, pela ficha do técnico ───────────────────
  //
  // A planilha de 2026 trazia seis notas: forehand e backhand somando
  // drive, smash e top spin, mais saque, bloqueio, recepção e cavada. Era
  // o resumo que cabia numa coluna de planilha.
  //
  // A ficha de avaliação que o Alexandre Monteiro usa tem dezenove: seis
  // golpes avaliados dos DOIS lados, e sete itens que não são golpe e
  // decidem ponto tanto quanto eles, da leitura do efeito da bola à
  // escolha do golpe para cada situação de jogo. É essa que está aqui.
  //
  // Golpe avaliado de um lado só perde o que o técnico vê: no Alexsandro
  // de agosto, o drive sai 7 de forehand e 6 de backhand, e a cavada sai
  // 5,5 de forehand e 7 de backhand, invertida. A média das duas esconde
  // as duas coisas, e é dela que sairia o treino da semana.
  "Tênis de Mesa": {
    avaliador: "Técnico",
    observa: [
      "Mecânica do movimento",
      "Regularidade do golpe",
      "Potência do golpe, por efeito e velocidade",
    ],
    // A escala é a mesma das outras duas, sem nada por cima. A ficha do
    // técnico chamava a primeira faixa de "iniciante" e dizia que os
    // valores eram referência para atletas amadores; a coordenação tirou
    // as duas coisas em 24/10/2026. Três modalidades lendo a mesma régua
    // com as mesmas palavras valem mais que o vocabulário de uma.
    //
    // O mecanismo de escala por modalidade continua em `escalaDe`: ele
    // existe para o dia em que uma delas precisar da régua própria, e
    // hoje nenhuma precisa.
    criterios: [
      ...golpesDosDoisLados([
        ["drive", "Drive"],
        ["smash", "Smash"],
        ["topSpin", "Top spin"],
        ["bloqueio", "Bloqueio"],
        ["harau", "Harau"],
        ["cavada", "Cavada"],
      ]),
      { id: "saque", rotulo: "Saque", grupo: "Jogo" },
      { id: "recepcaoDeSaque", rotulo: "Recepção de saque", grupo: "Jogo" },
      { id: "percepcaoDeEfeito", rotulo: "Percepção de efeito da bola", grupo: "Leitura" },
      { id: "percepcaoDeTrajetoria", rotulo: "Percepção de trajetória da bola", grupo: "Leitura" },
      { id: "movimentacao", rotulo: "Movimentação", grupo: "Jogo" },
      { id: "regularidadeEControle", rotulo: "Regularidade e controle", grupo: "Jogo" },
      {
        id: "escolhaDeGolpes",
        rotulo: "Escolha de golpes",
        grupo: "Leitura",
        detalhe: "A escolha de golpe adequada para cada situação de jogo",
      },
    ],
  },
  Skate: {
    avaliador: "Técnico",
    observa: ["Regularidade de manobra", "Potência de manobra", "Mecânica de movimento"],
    criterios: [
      { id: "ollie", rotulo: "Ollie" },
      { id: "flip", rotulo: "Flip" },
      { id: "heelflip", rotulo: "Heelflip" },
      { id: "fakieFlip", rotulo: "Fakie flip" },
      { id: "fakieHeelflip", rotulo: "Fakie heelflip" },
      { id: "kickflip", rotulo: "Kickflip" },
    ],
  },
  // ── BMX, quatro critérios e nenhum tempo ───────────────────
  //
  // A planilha tinha seis: os quatro trechos da pista que o técnico
  // avalia, mais os dois tempos cronometrados convertidos em nota. Os
  // dois tempos saíram daqui em 22/10/2026, por decisão da coordenação,
  // e continuam onde sempre deveriam estar: em testes físicos, medidos
  // em segundos.
  //
  // O motivo é que a conversão destruía justamente a comparação que a
  // avaliação existe para fazer. A nota do tempo era relativa ao GRUPO
  // daquele dia: o melhor tempo valia 10 e o pior valia 1, então um
  // atleta podia baixar o próprio tempo e perder nota porque os colegas
  // baixaram mais.
  //
  // E havia um efeito pior, que aparece no Benjamim. Ele tem 7 anos, é
  // da categoria Boys 5-6, e por isso era sempre o pior tempo da turma,
  // o que travava as duas notas dele em 1 nas três avaliações do ano.
  // As notas técnicas dele são 7, 7, 8 e 8. O escore dizia "regular"
  // sobre uma criança de 7 anos cujo gesto o técnico avalia como bom, e
  // dizia isso porque ela é mais nova que os colegas de pista.
  BMX: {
    avaliador: "Técnico",
    criterios: [
      {
        id: "largada",
        rotulo: "Largada",
        observa: ["Tempo de reação", "Transferência de força", "Estabilidade na saída"],
      },
      {
        id: "pedalada",
        rotulo: "1ª e 2ª pedalada",
        observa: ["Frequência de pedalada", "Aplicação de força", "Continuidade"],
      },
      {
        id: "curva",
        rotulo: "Entrada e saída de curva",
        observa: ["Entrada, pela linha escolhida", "Controle", "Saída, pela velocidade"],
      },
      {
        id: "salto",
        rotulo: "Salto",
        observa: ["Leitura de obstáculo", "Controle no ar", "Continuidade pós-salto"],
      },
    ],
  },
};

// ── Os critérios que saíram da ficha ────────────────────
//
// A ficha do tênis de mesa tinha seis critérios até agosto de 2026:
// "forehand" e "backhand" resumiam drive, smash e top spin de cada lado,
// e bloqueio e cavada vinham sem lado. A ficha de dezenove critérios que
// o técnico passou a usar não tem nenhum desses ids.
//
// As trinta avaliações já lançadas continuam com eles, porque cada
// avaliação guarda os critérios do dia, e isso é o que mantém a série
// comparável. O que elas perdiam era o GRUPO: o portal achava "saque" e
// "recepção de saque" no modelo novo e os punha em Jogo, e os quatro
// golpes ficavam num bloco sem título. Na tela e no PDF aparecia um
// grupo só, "Jogo", como se o resto não tivesse grupo nenhum.
//
// Esta tabela devolve o grupo a eles. Não é modelo: nada aqui entra em
// ficha nova, e é consultada só quando o id gravado não existe mais.
export const CRITERIOS_APOSENTADOS = {
  "Tênis de Mesa": {
    forehand: { rotulo: "Forehand", grupo: "Golpes básicos" },
    backhand: { rotulo: "Backhand", grupo: "Golpes básicos" },
    bloqueio: { rotulo: "Bloqueio", grupo: "Golpes básicos" },
    cavada: { rotulo: "Cavada", grupo: "Golpes básicos" },
  },
};

// ── Os critérios separados por grupo ───────────────────
//
// Por NOME do grupo, e não por vizinhança na lista. A ordem da planilha
// de 2026 alterna golpe e jogo, e agrupar só o que está lado a lado
// devolvia cinco blocos para três grupos, com "Golpes básicos" escrito
// três vezes.
//
// A ordem dos grupos é a da primeira aparição, e dentro de cada grupo a
// ordem da lista. No modelo os grupos já vêm juntos, então para uma ficha
// nova isto não muda nada: muda para as antigas, que é onde doía.
export function agruparPorGrupo(criterios) {
  const blocos = [];
  (criterios || []).forEach(c => {
    const grupo = c.grupo || null;
    const achado = blocos.find(b => b.grupo === grupo);
    if (achado) achado.itens.push(c);
    else blocos.push({ grupo, itens: [c] });
  });
  return blocos;
}

// O modelo de uma modalidade. Modalidade que o programa ainda não atende
// devolve lista vazia, e a tela pede os critérios em vez de inventar
// seis que ninguém combinou.
export function modeloDe(modalidade) {
  const m = MODELOS_DE_CRITERIO[modalidade];
  if (!m) return { avaliador: "Técnico", criterios: [], observa: [] };
  return { observa: [], ...m, criterios: m.criterios.map(c => ({ ...c })) };
}

// Os critérios que a tela oferece, prontos para gravar.
//
// O grupo vai junto, e não só o rótulo. Com dezenove critérios no tênis
// de mesa, é o grupo que separa golpe de leitura de jogo na tela; sem
// ele gravado, uma ficha antiga perderia a separação no dia em que o
// modelo mudasse, e viraria uma lista de dezenove linhas iguais.
export function criteriosSugeridos(modalidade) {
  return modeloDe(modalidade).criterios.map(c => {
    const saida = { id: c.id, rotulo: c.rotulo };
    if (c.grupo) saida.grupo = c.grupo;
    return saida;
  });
}

// ── Os critérios que a coordenação acrescenta ────────────────
//
// Os modelos acima são os que estão em uso. Eles não são a lista final:
// cada comissão acrescenta o que a modalidade dela pede, e o fez no
// papel durante anos. Por isso a configuração de um núcleo guarda só a
// DIFERENÇA em relação ao modelo — os critérios acrescentados e os
// desativados — e não uma cópia da lista inteira.
//
// Guardar a diferença é o que faz uma melhoria no modelo chegar a todos
// os núcleos. Se a configuração fosse uma cópia, o núcleo que mexeu uma
// vez nos critérios ficaria preso à ficha daquele dia, e a revisão do
// tênis de mesa de outubro de 2026 não teria chegado nele.
//
// Desativar NÃO apaga: a avaliação já gravada carrega os critérios dela
// dentro do documento, e continua compilando com eles. O que desativar
// faz é tirar o critério das PRÓXIMAS coletas, que é o que acontece
// quando a comissão para de avaliar uma coisa e passa a avaliar outra.

// O id de um critério novo, feito do rótulo. Ele vai para dentro de cada
// avaliação gravada e é a chave que liga a mesma coisa entre duas datas,
// por isso não pode ser aleatório: dois núcleos que criem "Saída de
// bloco" têm de poder comparar.
export function idDeCriterio(rotulo) {
  const limpo = String(rotulo || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!limpo.length) return "";
  return limpo
    .map((p, i) => (i === 0
      ? p.toLowerCase()
      : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join("");
}

// Os critérios de uma modalidade num núcleo: o modelo, menos os
// desativados, mais os acrescentados.
//
// `config` é o documento de configuração do núcleo, ou nada. Sem ele, a
// lista é o modelo puro, que é o que o portal fez até hoje.
export function criteriosDaModalidade(modalidade, config) {
  const base = modeloDe(modalidade).criterios;
  const desativados = new Set((config && config.desativados) || []);
  const extras = ((config && config.extras) || []).filter(c => c && c.id && c.rotulo);
  const vivos = base.filter(c => !desativados.has(c.id));
  // O extra que repete o id de um critério do modelo substitui o do
  // modelo em vez de duplicar a linha. É como a comissão renomeia um
  // critério sem perder a série: o id é o mesmo, o rótulo é outro.
  const porId = new Map(vivos.map(c => [c.id, c]));
  extras.forEach(c => porId.set(c.id, { ...porId.get(c.id), ...c }));
  // A ordem: os do modelo onde estavam, e os novos no fim, agrupados por
  // grupo. Critério novo que entra no meio da ficha confunde quem já
  // decorou a ordem de leitura.
  const idsDoModelo = vivos.map(c => c.id);
  const novos = extras.map(c => c.id).filter(id => !idsDoModelo.includes(id));
  return [...idsDoModelo, ...novos].map(id => ({ ...porId.get(id) }));
}

// Os grupos em uso numa modalidade, para a tela oferecer em vez de
// pedir que a coordenação escreva "Golpes básicos" de novo e erre o
// acento, o que criaria um grupo novo sem ninguém querer.
export function gruposDaModalidade(modalidade, config) {
  const grupos = [];
  criteriosDaModalidade(modalidade, config).forEach(c => {
    if (c.grupo && !grupos.includes(c.grupo)) grupos.push(c.grupo);
  });
  return grupos;
}

// Valida um critério que a coordenação está acrescentando.
//
// Devolve `{ erro }` ou `{ criterio }`. O id duplicado é o erro que
// importa: dois critérios com o mesmo id no mesmo documento fazem a
// média contar um deles duas vezes.
export function validarCriterioNovo({ rotulo, grupo, detalhe }, existentes = []) {
  const nome = String(rotulo || "").trim();
  if (nome.length < 3) return { erro: "O critério precisa de um nome de pelo menos 3 letras." };
  if (nome.length > 60) return { erro: "O nome do critério passou de 60 letras." };
  const id = idDeCriterio(nome);
  if (!id) return { erro: "O nome do critério precisa ter letras ou números." };
  const repetido = existentes.find(c => c.id === id);
  if (repetido) {
    return { erro: `Já existe um critério com esse nome: ${repetido.rotulo}.` };
  }
  const criterio = { id, rotulo: nome };
  if (String(grupo || "").trim()) criterio.grupo = String(grupo).trim();
  if (String(detalhe || "").trim()) criterio.detalhe = String(detalhe).trim();
  return { criterio };
}

// ── Uma avaliação compilada ──────────────────────────────────
//
// `avaliacao` é o documento como ele está gravado. `escalasDeFora` são
// as escalas de tempo da onda, usadas só quando o documento não trouxe
// as dele, que é o caso dos lançamentos antigos.
export function compilarAvaliacaoTecnica({ modalidade, avaliacao }) {
  const a = avaliacao || {};
  const mod = a.modalidade || modalidade || null;
  const modelo = modeloDe(mod);
  // A lista gravada é a que vale. Sem ela, cai no modelo da modalidade,
  // que é o que acontece com avaliação importada de planilha.
  const lista =
    Array.isArray(a.criterios) && a.criterios.length ? a.criterios : criteriosSugeridos(mod);
  const porModelo = Object.fromEntries(modelo.criterios.map(c => [c.id, c]));

  const aposentados = CRITERIOS_APOSENTADOS[mod] || {};
  const criterios = lista.map(bruto => {
    // O modelo de hoje primeiro; depois a tabela dos que saíram da ficha,
    // que é o que devolve o grupo às avaliações antigas.
    const base = porModelo[bruto.id] || aposentados[bruto.id] || {};
    // A marca de ausência do critério: o atleta não pôde ser avaliado
    // nele, por falta ou por dispensa médica. É diferente de em branco,
    // que é o avaliador não ter dado a nota.
    const ausencia = bruto.ausencia || null;
    const nota = numeroOuNulo(bruto.nota);
    return {
      id: bruto.id,
      rotulo: bruto.rotulo || base.rotulo || bruto.id,
      detalhe: bruto.detalhe || base.detalhe || null,
      observa: bruto.observa || base.observa || null,
      grupo: bruto.grupo || base.grupo || null,
      lado: bruto.lado || base.lado || null,
      gesto: bruto.gesto || base.gesto || null,
      nota,
      faixa: faixaDaNotaEm(nota, mod),
      ausencia: nota === null ? ausencia : null,
    };
  });

  const comNota = criterios.filter(c => Number.isFinite(c.nota));
  // ── O que entra no divisor, e o que fica de fora ───────────
  //
  // O divisor é o número de critérios da avaliação, e não o de critérios
  // com nota: avaliação com um critério em branco vale menos, porque o
  // gesto que não foi avaliado continua fazendo parte do esporte.
  //
  // A exceção é o critério com AUSÊNCIA registrada. "Em branco" e "não
  // correu, dispensa médica" são coisas diferentes: a primeira é falha
  // de avaliação e tem de pesar, a segunda é um fato sobre o dia e
  // baixar a média por causa dela seria contar ausência como nota zero,
  // que é justamente o que o painel não faz em teste físico nenhum.
  //
  // É também o que a planilha de 2026 fez, por outro caminho: no João
  // Pedro e na Melissa, que marcaram NA e DM nos tempos de janeiro, ela
  // imputou a média do próprio atleta nos dois critérios. A média sai
  // idêntica aos dois modos; o que muda é o somatório mostrado, e aqui
  // ele é o que foi avaliado de verdade.
  const ausentes = criterios.filter(c => !Number.isFinite(c.nota) && c.ausencia);
  const divisor = numeroOuNulo(a.divisor) > 0
    ? numeroOuNulo(a.divisor)
    : Math.max(1, lista.length - ausentes.length);

  // ── Por que o somatório é arredondado a UMA casa ───────────
  //
  // Porque é o número publicado. O somatório sai nos relatórios do
  // programa com uma decimal, e a média e o escore global derivam dele:
  // guardar precisão a mais aqui faria o painel discordar de um papel
  // que a coordenação já distribuiu.
  //
  // A diferença é pequena e real. A Carla de abril soma 36,962 e a
  // planilha publica 37,0; dividido por seis dá 6,16 contra 6,17, e o
  // escore global cai exatamente em 6,245 contra 6,25, que é a borda do
  // arredondamento: 6,2 de um lado, 6,3 do outro. Com o somatório
  // arredondado, os 87 escores de 2026 saem iguais aos publicados; sem
  // ele, dois saem um décimo abaixo.
  const somatorio = comNota.length ? arred(comNota.reduce((s, c) => s + c.nota, 0), 1) : null;

  return {
    data: a.data || null,
    modalidade: mod,
    avaliador: a.avaliador || null,
    tempoDePraticaMeses: numeroOuNulo(a.tempoDePraticaMeses),
    observacoes: a.observacoes || "",
    observa: modelo.observa || [],
    // ── O período de observação ───────────────────────────────
    //
    // A ficha do tênis de mesa tem "Período de Avaliação (tempo de
    // treino)", e o Alexandre escreveu "5 a 12/8" na do Alexsandro: a
    // nota é o que ele viu numa SEMANA de treino, e não num dia.
    //
    // A data continua sendo a chave da avaliação, porque é por ela que a
    // série se ordena e que o documento é identificado. O período entra
    // ao lado, e muda o que a folha diz de si mesma: nota de uma semana
    // de observação não se lê como nota de um teste cronometrado.
    periodoDe: a.periodoDe || null,
    periodoAte: a.periodoAte || null,
    // A escala desta modalidade, com os nomes que ela dá às faixas, para
    // a tela e o papel mostrarem a legenda certa sem redescobri-la.
    escala: escalaDe(mod),
    criterios,
    somatorio,
    divisor,
    maximo: divisor * NOTA_MAXIMA,
    media: somatorio !== null && divisor ? arred(somatorio / divisor, 2) : null,
    completa: lista.length > 0 && comNota.length + ausentes.length === lista.length,
    faltam: criterios
      .filter(c => !Number.isFinite(c.nota) && !c.ausencia)
      .map(c => c.rotulo),
    // Os que não valem falta, com o motivo: eles saíram do divisor e
    // quem lê precisa saber que a média foi tirada de menos critérios.
    ausentes: ausentes.map(c => ({ rotulo: c.rotulo, motivo: rotuloDaAusencia(c.ausencia) })),
  };
}

// ── A série de um atleta ─────────────────────────────────────

export function compilarSerieTecnica({ modalidade, avaliacoes = [] }) {
  const ordenadas = [...avaliacoes]
    .filter(a => a && a.data)
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));

  let anterior = null;
  let primeira = null;
  return ordenadas.map(av => {
    const atual = compilarAvaliacaoTecnica({ modalidade, avaliacao: av });
    const ponto = {
      ...atual,
      delta: anterior ? variacaoTecnica(anterior, atual) : null,
      desdeAPrimeira: primeira ? variacaoTecnica(primeira, atual) : null,
      dataDaPrimeira: primeira ? primeira.data : atual.data,
    };
    anterior = atual;
    if (!primeira) primeira = atual;
    return ponto;
  });
}

// O que mudou de uma avaliação para outra, critério por critério.
//
// A comparação é por id de critério, e não por posição na lista. Troca
// de critério entre duas datas aparece em `criteriosNovos` e
// `criteriosSaidos`, em vez de alinhar manobra com golpe.
function variacaoTecnica(de, para) {
  const antes = Object.fromEntries(de.criterios.map(c => [c.id, c]));
  const depois = Object.fromEntries(para.criterios.map(c => [c.id, c]));
  const ids = [...new Set([...Object.keys(antes), ...Object.keys(depois)])];
  return {
    media: dif(de.media, para.media),
    somatorio: dif(de.somatorio, para.somatorio),
    porCriterio: ids
      .filter(id => antes[id] && depois[id])
      .map(id => ({
        id,
        rotulo: depois[id].rotulo,
        de: antes[id].nota,
        para: depois[id].nota,
        delta: dif(antes[id].nota, depois[id].nota),
      })),
    criteriosNovos: ids.filter(id => !antes[id]).map(id => depois[id].rotulo),
    criteriosSaidos: ids.filter(id => !depois[id]).map(id => antes[id].rotulo),
  };
}

// ── A leitura em texto ───────────────────────────────────────
//
// Seis notas não dizem a um técnico o que ele já não sabia, e dizem
// pouco a quem não acompanha o treino. O laudo aponta onde está o gesto
// mais firme e onde está o que falta, que é a pergunta que a coordenação
// faz na frente desta tela.
//
// Ele não compara o atleta com os colegas nem com referência de
// modalidade, porque nota de critério é escala do avaliador, e dois
// técnicos calibram diferente. Serve para acompanhar o mesmo atleta.
export function laudoTecnico(ponto) {
  if (!ponto) return null;
  const comNota = ponto.criterios.filter(c => Number.isFinite(c.nota));
  if (!comNota.length) return null;
  const ordenados = [...comNota].sort((a, b) => b.nota - a.nota);
  const melhor = ordenados[0];
  const atras = ordenados[ordenados.length - 1];
  const faixa = faixaDaNotaEm(ponto.media, ponto.modalidade);

  const partes = [
    `A média dos ${ponto.divisor} critérios é ${virgula(ponto.media)}, na faixa `
    + `${(faixa ? faixa.rotulo : "sem faixa").toLowerCase()} da escala.`,
  ];
  if (ordenados.length > 1 && melhor.nota !== atras.nota) {
    partes.push(
      `O critério mais firme é ${melhor.rotulo.toLowerCase()}, com ${virgula(melhor.nota)}, `
      + `e o que está mais atrás é ${atras.rotulo.toLowerCase()}, com ${virgula(atras.nota)}.`,
    );
  } else if (ordenados.length > 1) {
    partes.push("Os critérios saíram todos na mesma nota, sem um gesto à frente do outro.");
  }
  if (ponto.faltam.length) {
    partes.push(`Ficaram sem nota: ${ponto.faltam.join(", ").toLowerCase()}.`);
  }

  return {
    classificacao: faixa ? faixa.rotulo : null,
    melhor: melhor.rotulo,
    atras: atras.rotulo,
    texto: partes.join(" "),
    // A ressalva anda junto do laudo em todo lugar onde ele aparece,
    // como a do somatotipo. Sem ela, "intermediário" vira carimbo.
    ressalva:
      "A nota resulta da observação do técnico sobre o gesto, numa escala de 1 a 10, e "
      + "não de medida instrumental. Serve para acompanhar o mesmo atleta ao longo do "
      + "tempo, com o mesmo avaliador, e não para comparar atletas entre si.",
  };
}

// ── O grupo ──────────────────────────────────────────────────

// A média da modalidade numa data, critério por critério.
//
// Serve para a tela dizer onde a turma inteira está atrás, que é
// informação de planejamento de treino e não de atleta. Vem com `n`,
// porque média de três atletas e média de dez não se comparam.
export function mediaDaOnda({ modalidade, avaliacoes = [] }) {
  const pontos = avaliacoes.map(avaliacao =>
    compilarAvaliacaoTecnica({ modalidade, avaliacao }));
  const medias = pontos.map(p => p.media).filter(Number.isFinite);
  const porCriterio = {};
  pontos.forEach(p => {
    p.criterios.forEach(c => {
      if (!Number.isFinite(c.nota)) return;
      (porCriterio[c.id] = porCriterio[c.id] || { rotulo: c.rotulo, notas: [] }).notas.push(c.nota);
    });
  });
  return {
    media: medias.length ? arred(medias.reduce((s, v) => s + v, 0) / medias.length, 2) : null,
    n: medias.length,
    porCriterio: Object.entries(porCriterio).map(([id, { rotulo, notas }]) => ({
      id,
      rotulo,
      media: arred(notas.reduce((s, v) => s + v, 0) / notas.length, 2),
      n: notas.length,
    })),
  };
}

// ── As marcas de ausência ────────────────────────────────────
//
// As mesmas de `testesFisicos.js`, mais o "NA" que a planilha de
// performance usava. Um critério com ausência registrada sai do divisor
// da média: o atleta não deixou de ser avaliado por descuido, ele não
// pôde ser avaliado naquele dia.
const AUSENCIAS_DO_CRITERIO = {
  na: { codigo: "naoRealizado", rotulo: "Não realizado" },
  x: { codigo: "naoRealizado", rotulo: "Não realizado" },
  f: { codigo: "falta", rotulo: "Falta" },
  dm: { codigo: "dispensaMedica", rotulo: "Dispensa médica" },
};

export function marcaDeAusencia(bruto) {
  const texto = String(bruto === null || bruto === undefined ? "" : bruto).trim().toLowerCase();
  const marca = AUSENCIAS_DO_CRITERIO[texto];
  return marca ? marca.codigo : null;
}

export function rotuloDaAusencia(codigo) {
  const marca = Object.values(AUSENCIAS_DO_CRITERIO).find(m => m.codigo === codigo);
  return marca ? marca.rotulo : "Sem registro";
}

// ── Apoio ────────────────────────────────────────────────────

function arred(n, casas = 1) {
  const v = numeroOuNulo(n);
  if (v === null) return null;
  const f = Math.pow(10, casas);
  return Math.round(v * f) / f;
}

function dif(a, b) {
  return Number.isFinite(a) && Number.isFinite(b) ? arred(b - a, 2) : null;
}

function numeroOuNulo(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function virgula(n) {
  return Number.isFinite(n) ? String(n).replace(".", ",") : "sem nota";
}

// O tempo de prática, em texto. Guardado em meses porque atleta de base
// entra com poucos meses de esporte e a diferença entre quatro e dez
// meses é grande; mostrado em anos quando passa de um, porque é assim
// que o técnico fala.
export function tempoDePraticaEmTexto(meses) {
  const m = numeroOuNulo(meses);
  if (m === null || m < 0) return null;
  if (m < 12) return `${m} ${m === 1 ? "mês" : "meses"}`;
  const anos = Math.floor(m / 12);
  const resto = m % 12;
  const parteAnos = `${anos} ${anos === 1 ? "ano" : "anos"}`;
  if (!resto) return parteAnos;
  return `${parteAnos} e ${resto} ${resto === 1 ? "mês" : "meses"}`;
}
